import styled from '@emotion/styled';
import shallow from 'zustand/shallow';

import { useIsAuthenticated } from '@updater/auth';
import {
  theme as UdsTheme,
  Card as UdsCard,
  ThemeOverride,
  ThemeProvider,
} from '@updater/ui-uds';
import { ErrorBoundary, Tracker, GoogleTagManager } from 'services';
import { resolveMicrositeConfig } from 'config/microsites';
import { resolveConfigState } from './resolveConfigState';

import { useQual, useStore, useRemoteConfig } from 'hooks';
import { MicrositeContext } from 'context';
import { useEffect } from 'react';
import { Layout, Head, OffersContextWrapper, QualErrorModal } from 'components';
import { Fullstory } from 'services/fullstory/Fullstory';
import { IdentityInit } from 'services/identity';
import {
  WhitelistGuard,
  reportHostAccessError,
} from 'components/WhitelistGuard/WhitelistGuard';

const Card = styled(UdsCard)`
  display: grid;
  place-items: center;
  gap: ${({ theme }) => `${theme.space.s}px`};
  padding: ${({ theme }) => `${theme.space.l}px`};
  box-shadow: 0px 1px 16px -14px black;
  margin: ${({ theme }) => `${theme.space.s}px`};
`;

const Container = styled.div`
  display: grid;
  place-items: center;
  height: 500px;
  max-height: 50vh;
  border: ${({ theme }) => theme.colors.border};
`;

const NoProvider = () => {
  return (
    <Container>
      <Card>No provider in query</Card>
    </Container>
  );
};

export function ProviderWrapper({ children }) {
  const { promoId, microsite, provider, uuid } = useQual();
  const [reskinProvider] = useStore((store) => [store.reskinProvider], shallow);
  useEffect(() => {
    console.log('[ProviderApp] appVersion:', process.env.NEXT_PUBLIC_GIT_SHA);
  }, []);

  // Remote config: for qual.promoId values not present in the local mapping,
  // fetch config from Cloudflare. These are fully-remote partners.
  const {
    config: remoteConfig,
    loading: remoteLoading,
    isEligible: remoteEligible,
    configError: remoteConfigError,
  } = useRemoteConfig(promoId);

  // Local config: resolves a bundled config from the qual's config key fields,
  // checked in priority order:
  //   qual.promoId (when mapped in promoIdToMicrosite) → qual.microsite (WordPress key)
  //   → qual.provider (oldest legacy key from Shop App — unrelated to ISP "provider")
  const {
    config: localConfig,
    key: localConfigKey,
    source,
  } = resolveMicrositeConfig({ promoId, microsite, provider });

  const micrositeConfig = remoteConfig ?? localConfig;
  const configKey = remoteConfig ? promoId : localConfigKey;
  const qualKey = promoId ?? microsite ?? provider;

  let { error, theme } = resolveConfigState({
    remoteEligible,
    remoteLoading,
    remoteConfig,
    remoteConfigError,
    source,
    micrositeConfig,
    configKey,
    qualKey,
  });
  if (!uuid) error = 'NO_UUID';

  // Theme overrides: if the user is on a single-provider site (e.g. xfinity)
  // and they are *NOT* eligible for that provider, we can sell them other
  // offers from providers of the same type (MSO or Telco).  We reskin the whole
  // experience with the new provider when this happens, but the microsite
  // config, the promo ID and the rest of the data remain the same.  This
  // is a visual reskin only!
  let overrideTitle;
  if (reskinProvider) {
    console.log(
      `[ProviderWrapper] overriding theme with ${reskinProvider.reskinTheme?.customTheme?.name} reskin`
    );
    theme = reskinProvider.reskinTheme;
    overrideTitle = reskinProvider.reskinTitle;
  }

  const isAuthenticated = useIsAuthenticated();

  return (
    <MicrositeContext.Provider value={micrositeConfig}>
      <IdentityInit />
      <Fullstory>
        <ThemeProvider customTheme={theme as ThemeOverride} selfHostedFonts>
          {!error && (!remoteEligible || !remoteLoading) && (
            <Tracker>
              <Head overrideTitle={overrideTitle} />
              <GoogleTagManager />
              <OffersContextWrapper>
                <Layout>
                  <ErrorBoundary onError={reportHostAccessError}>
                    <WhitelistGuard>
                      {!isAuthenticated ? null : !configKey ? (
                        <NoProvider />
                      ) : (
                        children
                      )}
                    </WhitelistGuard>
                  </ErrorBoundary>
                </Layout>
              </OffersContextWrapper>
            </Tracker>
          )}
          {error && <QualErrorModal reason={error} />}
        </ThemeProvider>
      </Fullstory>
    </MicrositeContext.Provider>
  );
}
